import { isPlatformBrowser } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, interval, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { HorseRacingService } from 'src/app/services/horse-racing.service';
import { Country } from 'src/app/shared/enums/countries.enum';
import { RouterFlowService } from 'src/app/shared/services/router-flow.service';
import { SettingsService } from '../../services/settings.service';
@Component({
  selector: 'app-opportunity',
  templateUrl: './opportunity.component.html',
  styleUrls: ['./opportunity.component.scss'],
})
export class OpportunityComponent implements OnInit, OnDestroy {
  @Output() raceDetailsEvent = new EventEmitter();

  private subscription: Subscription;
  nextBettingOpportunities;
  races;
  nextPMUOpportunities;
  racesPMU;
  today; tomorrow; yesterday;
  format = 'yyyy-MM-dd';
  locale = 'en-US';
  loading = true;
  selectedTabIndex = 1;
  tabFirstInit = false;

  get showPMUTab(): boolean {
    return Boolean(this.nextPMUOpportunities.length);
  }
  get opportunities(): any {
    return this.selectedTabIndex === 0 ? this.nextPMUOpportunities : this.nextBettingOpportunities;
  }

  get goUrl(): string {
    return this.selectedTabIndex === 0 ? '/next-races/pmu' : '/next-races/all';
  }

  @Input() page: string;
  @Input() sideCol: boolean = false;
  @Input() collapsed = false;
  constructor(
    public router: Router,
    private horseRacingService: HorseRacingService,
    private routerFlowService: RouterFlowService,
    private authService: AuthService,
    private settingServ: SettingsService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.authService.currentAuthStatus.subscribe((authStatus) => {
      if (!authStatus && this.subscription) {
        this.subscription.unsubscribe();
      }
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.selectedTabIndex = this.page === 'all' ? 1 : 0;
    this.getOpportunities();
  }

  getOpportunities(): void {
    combineLatest([
      this.horseRacingService.getNextBettingOpportunities(),
      this.horseRacingService.getNextPMUOpportunities()
    ]).pipe(
      map((values) => ({ betting: values[0], pmu: values[1] })),
      tap(({ betting, pmu }) => {
        this.races = betting.filter((race) => !this.settingServ.filterRaceCountries(race.Country));
        this.racesPMU = pmu.filter((race) => !this.settingServ.filterRaceCountries(race.Country));
        this.setLocalDate(this.races);
        this.setLocalDate(this.racesPMU);
        if (isPlatformBrowser(this.platformId)) {
          this.subscription = interval(10000).subscribe(x => { this.getTimeDifference(); });
          if (!this.sideCol) {
            this.nextBettingOpportunities = this.page === 'home' ? this.races.slice(0, 10) : this.races.slice(0, 40);
            this.nextPMUOpportunities = this.page === 'home' ? this.racesPMU.slice(0, 10) : this.racesPMU.slice(0, 40);
          } else {
            this.nextBettingOpportunities = this.races.slice(0, 4);
            this.nextPMUOpportunities = this.racesPMU.slice(0, 4);
          }
          if (this.page === 'home' && !this.tabFirstInit && Boolean(this.nextPMUOpportunities.length)) {
            this.selectedTabIndex = 0;
            this.tabFirstInit = true;
          }
          if (!this.racesPMU.length) {
            this.selectedTabIndex = 1
          }
          this.loading = false;
        }
      })
    ).subscribe();
  }

  setLocalDate(races): void {
    for (const race of races) {
      const startTimeUtc = race.StartTimeUtcTimestamp ? new Date(race.StartTimeUtcTimestamp * 1000) : undefined;
      const scheduleTimeUtc = race.ScheduleTimeUtcTimestamp ? new Date(race.ScheduleTimeUtcTimestamp * 1000) : undefined;
      const localDate = (startTimeUtc !== undefined) ? startTimeUtc : scheduleTimeUtc;
      race.localDatetime = localDate !== null ? localDate : '';
      const startTime = new Date();
      const difference = race.localDatetime.getTime() - startTime.getTime();
      race.dateDiffMin = Math.round(difference / 60000);
      race.dateDiffHour = Math.round(difference / (60000 * 60));
    }
  }

  getTimeDifference(): void {
    for (const race of this.races) { this.getRaceTimeDifference(race); }
    for (const race of this.racesPMU) { this.getRaceTimeDifference(race); }

    const filteredRaces = this.races.filter((r) => this.checkDiffTimeStamp(r))
    const filteredPMURaces = this.racesPMU.filter((r) => this.checkDiffTimeStamp(r));


    this.racesPMU = filteredPMURaces;
    this.nextPMUOpportunities = this.sideCol ? this.racesPMU.slice(0, 4) :
      this.page === 'home' ? this.racesPMU.slice(0, 10) : this.racesPMU.slice(0, 40);
    if (filteredRaces.length <= 41) {
      this.subscription.unsubscribe();
      this.getOpportunities();
    } else if (this.races.length !== filteredRaces.length) {
      this.races = filteredRaces;
      this.nextBettingOpportunities = this.sideCol ? this.races.slice(0, 4) :
        this.page === 'home' ? this.races.slice(0, 10) : this.races.slice(0, 40);
    }
  }

  getRaceTimeDifference(race: any): void {
    const startTime = new Date();
    const difference = race.localDatetime.getTime() - startTime.getTime();
    race.dateDiffMin = Math.round(difference / 60000);
    race.dateDiffHour = Math.round(difference / (60000 * 60));
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  goToRaceDetails(race): void {
    this.routerFlowService.sendRedirectInfo();
    this.page === 'home' ? this.raceDetailsEvent.emit() : this.loading = true;
    const date = new Date(race.ScheduleTimeUtcTimestamp * 1000);
    const year = date.getFullYear();
    const month = (1 + date.getMonth()).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    const shortDate = day + '-' + month + '-' + year;
    const urlPath = '/race/' + race.Country + '/' + shortDate + '/' + race.Track + '/' + race.RaceId;
    this.sideCol ? this.router.navigateByUrl('/race', { skipLocationChange: true }).then(() =>
      this.router.navigate([urlPath])) :
      this.router.navigateByUrl(urlPath);
  }

  updateNextRaces(type: number): void {
    this.selectedTabIndex = type;
  }

  getFlagSlug(country): string {
    const countrySlug = country.replace(/ /g, '');
    const countryCode = Country[countrySlug] !== undefined ? Country[countrySlug] : 'GR';
    const countryCodeLowerCase = countryCode.toLowerCase();
    return 'assets/media/flags/' + countryCodeLowerCase + '.svg';
  }

  getRaceType(race): 'Trot' | 'Gallop' | 'Greyhound' {
    return race.IsTrot ? 'Trot' : race.IsGallop ? 'Gallop' : 'Greyhound';
  }

  checkDiffTimeStamp(race) {
    const scheduleTimeUtc = race.ScheduleTimeUtcTimestamp
      ? new Date(race.ScheduleTimeUtcTimestamp * 1000)
      : undefined;
    const startTime = new Date();
    if (startTime.getTime() >= scheduleTimeUtc.getTime()) {
      return false
    } else {
      return true
    }

  }

}
